import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Cloud, Shield, Briefcase, Cog, ArrowRight } from 'lucide-react';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay },
});

const scaleIn = (delay = 0) => ({
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay },
});

const floatAnim = (y = 15, duration = 5) => ({
  animate: { y: [0, -y, 0] },
  transition: { duration, ease: 'easeInOut', repeat: Infinity },
});

export default function Hero() {
  const MotionLink = motion.create(Link);

  return (
    <section className="hero-v2">
      {/* Animated gradient background */}
      <div className="hero-v2__bg">
        <div className="hero-v2__gradient hero-v2__gradient--1" />
        <div className="hero-v2__gradient hero-v2__gradient--2" />
        <div className="hero-v2__gradient hero-v2__gradient--3" />
        <div className="hero-v2__grid" />
      </div>

      <div className="container">
        <div className="hero-v2__wrapper">
          {/* Main content */}
          <motion.div className="hero-v2__content" {...fadeUp(0.1)}>
            <h1 className="hero-v2__title">
              We Build the Digital
              <br />
              <span className="hero-v2__highlight">Infrastructure</span>
              <br />
              of <span className="hero-v2__tomorrow">Tomorrow</span>
            </h1>

            <p className="hero-v2__subtitle">
              Expert IT consulting, cloud solutions, and digital transformation
              for businesses that want to Lead and not Follow.
            </p>

            <motion.div className="hero-v2__actions" {...fadeUp(0.3)}>
              <MotionLink
                to="/contact"
                className="hero-v2__btn hero-v2__btn--primary"
                whileHover={{ scale: 1.05, boxShadow: '0 20px 50px rgba(37,99,235,0.4)' }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Start Your Project</span>
                <ArrowRight size={18} style={{ display: 'inline-flex' }} />
              </MotionLink>
              <MotionLink
                to="/services"
                className="hero-v2__btn hero-v2__btn--secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Explore Services</span>
                <ArrowRight size={18} style={{ display: 'inline-flex' }} />
              </MotionLink>
            </motion.div>
          </motion.div>

          {/* Right side - Visual showcase */}
          <motion.div className="hero-v2__visual" {...scaleIn(0.3)}>
            <div className="hero-v2__cards">
              {/* Main large card */}
              <motion.div
                className="hero-v2__card hero-v2__card--main"
                {...floatAnim(12, 6)}
              >
                <div className="hero-v2__card-content">
                  <div className="hero-v2__card-icon"><Cloud size={32} /></div>
                  <h3 className="hero-v2__card-title">Cloud Migration</h3>
                  <p className="hero-v2__card-desc">AWS & Azure infrastructure</p>
                  <div className="hero-v2__card-stats">
                    <div>
                      <div className="hero-v2__stat-val">78%</div>
                      <div className="hero-v2__stat-label">Faster</div>
                    </div>
                    <div>
                      <div className="hero-v2__stat-val">40%</div>
                      <div className="hero-v2__stat-label">Cheaper</div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Top accent card */}
              <motion.div
                className="hero-v2__card hero-v2__card--accent hero-v2__card--left"
                {...floatAnim(10, 5)}
              >
                <div className="hero-v2__card-icon"><Shield size={28} /></div>
                <h4>Security</h4>
                <p>Zero breaches</p>
              </motion.div>

              {/* Right accent card */}
              <motion.div
                className="hero-v2__card hero-v2__card--accent hero-v2__card--right"
                {...floatAnim(8, 4.5)}
              >
                <div className="hero-v2__card-icon"><Briefcase size={28} /></div>
                <h4>Enterprise</h4>
                <p>Application Grade</p>
              </motion.div>

              {/* Bottom accent card */}
              <motion.div
                className="hero-v2__card hero-v2__card--accent hero-v2__card--bottom"
                {...floatAnim(9, 5.5)}
              >
                <div className="hero-v2__card-icon"><Cog size={28} /></div>
                <h4>DevOps</h4>
                <p>10x releases</p>
              </motion.div>
            </div>

            {/* Floating elements */}
            <motion.div
              className="hero-v2__float hero-v2__float--1"
              animate={{ y: [0, -30, 0], x: [0, 15, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="hero-v2__float hero-v2__float--2"
              animate={{ y: [0, 25, 0], x: [0, -20, 0] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="hero-v2__float hero-v2__float--3"
              animate={{ y: [0, -25, 0], x: [0, -15, 0] }}
              transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="hero-v2__float hero-v2__float--4"
              animate={{ y: [0, 20, 0], x: [0, 10, 0] }}
              transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
